import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { money } from '../lib/format';
import { publicQuoteUrl } from '../lib/quoteShareUrl';
import { supabase } from '../lib/supabase';
import { useReportHeadMeta } from '../lib/useReportHeadMeta';
import '../styles/public-resale-report.css';
import '../styles/public-quote.css';

const COPY = {
  en: {
    title: 'Service quotation',
    inspectionTitle: 'Inspection quotation',
    vehicle: 'Vehicle',
    lines: 'Line items',
    total: 'Total',
    approved: 'Approved total',
    note: 'Workshop note',
    visit: 'Visit',
    status: 'Status',
    notFound: 'Quote not found or link has expired.',
    loading: 'Loading quotation…',
    plate: 'Plate',
    qty: 'Qty',
    unit: 'Unit',
    amount: 'Amount',
    disclaimer:
      'This quotation is valid for the listed work only. Prices may change if additional work is required.',
    statuses: {
      sent: 'Awaiting reply',
      approved: 'Approved',
      rejected: 'Rejected',
      partial: 'Partially approved',
    },
  },
  ar: {
    title: 'عرض سعر للخدمة',
    inspectionTitle: 'عرض سعر للفحص',
    vehicle: 'السيارة',
    lines: 'البنود',
    total: 'الإجمالي',
    approved: 'المعتمد',
    note: 'ملاحظة الورشة',
    visit: 'الزيارة',
    status: 'الحالة',
    notFound: 'عرض السعر غير موجود أو انتهت صلاحية الرابط.',
    loading: 'جارٍ تحميل عرض السعر…',
    plate: 'اللوحة',
    qty: 'الكمية',
    unit: 'السعر',
    amount: 'المبلغ',
    disclaimer:
      'عرض السعر هذا يشمل الأعمال المذكورة فقط. قد تتغير الأسعار إذا تطلب الأمر أعمالاً إضافية.',
    statuses: {
      sent: 'بانتظار الرد',
      approved: 'معتمد',
      rejected: 'مرفوض',
      partial: 'معتمد جزئياً',
    },
  },
};

function vehicleTitle(vehicle) {
  if (!vehicle) return '—';
  return [vehicle.brand, vehicle.model, vehicle.year].filter(Boolean).join(' ') || '—';
}

export default function PublicQuotePage() {
  const { token } = useParams();
  const [lang, setLang] = useState('en');
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');

  const t = COPY[lang] || COPY.en;

  useEffect(() => {
    let alive = true;
    async function load() {
      setError('');
      setQuote(null);
      const { data, error: rpcError } = await supabase.rpc('get_public_quotation', {
        p_token: token,
      });
      if (!alive) return;
      if (rpcError) {
        setError(rpcError.message || t.notFound);
        return;
      }
      setQuote(data);
    }
    load();
    return () => {
      alive = false;
    };
  }, [token, t.notFound]);

  const ogMeta = useMemo(() => {
    if (!quote) return null;
    const vehicle = vehicleTitle(quote.vehicle);
    const brand = quote.business?.brand_name || 'BW Station';
    const titleKind =
      quote.quote_kind === 'inspection' ? 'Inspection quotation' : 'Service quotation';
    return {
      title: `${titleKind} · ${vehicle} · ${brand}`,
      description: [
        vehicle,
        quote.total != null ? money(quote.total, 2) : null,
        quote.business?.brand_name,
      ]
        .filter(Boolean)
        .join(' · '),
      pageUrl: publicQuoteUrl(token),
      siteName: brand,
      image: quote.business?.logo_url || undefined,
      lang: lang === 'ar' ? 'ar' : 'en',
      locale: lang === 'ar' ? 'ar_EG' : 'en_US',
    };
  }, [quote, token, lang]);

  useReportHeadMeta(ogMeta);

  if (error) {
    return (
      <div className="pub-report-state" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div>
          <p className="error">{t.notFound}</p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="pub-report-state" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div>
          <div className="pub-report-spinner" aria-hidden="true" />
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  const business = quote.business || {};
  const branch = quote.branch || {};
  const vehicle = quote.vehicle || {};
  const items = quote.items || [];
  const brandInitial = (business.brand_name || 'W').trim().charAt(0).toUpperCase();
  const title =
    quote.quote_kind === 'inspection' ? t.inspectionTitle : t.title;
  const sentDate = quote.sent_at
    ? new Date(quote.sent_at).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;
  const statusLabel = t.statuses[quote.status] || quote.status;

  return (
    <div className="pub-report pub-quote" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="pub-report-shell">
        <header className="pub-report-topbar">
          <div className="pub-report-brand">
            {business.logo_url ? (
              <img className="pub-report-logo" src={business.logo_url} alt="" />
            ) : (
              <div className="pub-report-logo-fallback" aria-hidden="true">
                {brandInitial}
              </div>
            )}
            <div className="pub-report-brand-copy">
              <strong>{business.brand_name || 'BW Station'}</strong>
              <span>
                {[branch.name, branch.address].filter(Boolean).join(' · ') ||
                  business.address ||
                  business.legal_name}
              </span>
            </div>
          </div>
          <div className="pub-report-lang" role="group" aria-label="Language">
            <button
              type="button"
              className={lang === 'en' ? 'active' : ''}
              onClick={() => setLang('en')}
            >
              EN
            </button>
            <button
              type="button"
              className={lang === 'ar' ? 'active' : ''}
              onClick={() => setLang('ar')}
            >
              ع
            </button>
          </div>
        </header>

        <section className="pub-quote-hero">
          <p className="pub-report-kicker">{title}</p>
          <h1>{quote.vehicle_label || vehicleTitle(vehicle)}</h1>
          <div className="pub-quote-meta">
            {vehicle.plate_number ? (
              <span>
                {t.plate}: {vehicle.plate_number}
              </span>
            ) : null}
            {sentDate ? <span>{sentDate}</span> : null}
            <span>{statusLabel}</span>
          </div>
        </section>

        <section className="pub-quote-card">
          <header className="pub-quote-card-head">
            <h2>{t.lines}</h2>
            <strong>{money(quote.total, 2)}</strong>
          </header>
          <ul className="pub-quote-lines">
            {items.map((item, index) => (
              <li key={`${item.description}-${index}`}>
                <div>
                  <strong>{item.description || '—'}</strong>
                  <span>
                    {item.quantity || 1} × {money(item.unit_price, 2)}
                  </span>
                </div>
                <b>{money(item.price, 2)}</b>
              </li>
            ))}
          </ul>
          <footer className="pub-quote-total">
            <span>{t.total}</span>
            <strong>{money(quote.total, 2)}</strong>
          </footer>
          {quote.status !== 'sent' && Number(quote.approved_total) > 0 ? (
            <footer className="pub-quote-total approved">
              <span>{t.approved}</span>
              <strong>{money(quote.approved_total, 2)}</strong>
            </footer>
          ) : null}
        </section>

        {quote.note ? (
          <section className="pub-quote-note">
            <h2>{t.note}</h2>
            <p>{quote.note}</p>
          </section>
        ) : null}

        <p className="pub-quote-disclaimer">{t.disclaimer}</p>

        {(business.phone_primary || branch.phone || business.support_email) && (
          <footer className="pub-quote-contact">
            {[business.phone_primary || branch.phone, business.support_email]
              .filter(Boolean)
              .join(' · ')}
          </footer>
        )}
      </div>
    </div>
  );
}
